import React, { useState } from 'react';
import { Drawer, Box, List, ListItemButton, ListItemIcon, ListItemText, Icon, IconButton, Typography } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import config from '../../Config/config.json';
import { getLocalValue, IsSmallScreen, LoacalVariables, } from '../../commonFunctions/CommonFunctions';
import { getMenu } from './menu';
import logoImage from '../../images/logo.svg'

interface MenuItem {
  id: number;
  label: string;
  path?: string;
  icon: React.ElementType;
  children?: MenuItem[];
}

interface SidebarProps {
  isCollapsed: boolean;
  sx?: object;
  colors: {
    sidebarBg: string;
    contentBg: string;
    accent: string;
  };
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, sx, colors }) => {
  const userType = getLocalValue(LoacalVariables.UserType);
  const year = new Date().getFullYear();
  
  const [openChildMenus, setOpenChildMenus] = useState<Record<number, boolean>>({});
  const [hoveredMenuId, setHoveredMenuId] = useState<number | null>(null);
  
  const location = useLocation();  // Get the current location from React Router
  const navigate = useNavigate();
  const isMobile = IsSmallScreen();


  const handleToggleChildMenu = (id: number) => {
    setOpenChildMenus((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleMouseEnter = (id: number) => {
    if (isCollapsed) {
      setHoveredMenuId(id);
    }
  };

  const handleMouseLeave = () => {
    setHoveredMenuId(null);
  };

  const menu = getMenu(userType);

  // Helper function to determine if the menu item is selected
  const isSelected = (path?: string) => {
    return path && location.pathname === path;
  };

  const renderMenu = (menuItem: MenuItem) => (
    <li
      key={menuItem.id}
      onMouseEnter={() => handleMouseEnter(menuItem.id)}
      onMouseLeave={handleMouseLeave}
      style={{ position: 'relative' }}
    >
      <ListItemButton
        component={Link}
        to={menuItem.path || '#'}
        onClick={() => menuItem.children && handleToggleChildMenu(menuItem.id)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          margin: '4px 8px',
          borderRadius: '8px',
          backgroundColor: isSelected(menuItem.path) ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
          color: isSelected(menuItem.path) ? '#fff' : 'rgba(255, 255, 255, 0.7)',
          transition: 'all 0.2s ease-in-out',
          border: isSelected(menuItem.path) ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid transparent',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            transform: 'translateX(4px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
          '&:active': {
            transform: 'translateX(2px)',
          },
        }}
      >
        {/* Icon with better visual hierarchy */}
        {((!isMobile && isCollapsed) || (!isMobile && !isCollapsed) || (isMobile && !isCollapsed)) && (
          <ListItemIcon sx={{ minWidth: '40px' }}>
            <menuItem.icon
              sx={{
                color: isSelected(menuItem.path) ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '20px'
              }}
            />
          </ListItemIcon>
        )}

        {/* Menu label with smooth animation */}
        {!isCollapsed && (
          <ListItemText
            primary={menuItem.label}
            sx={{
              '& .MuiTypography-root': {
                fontSize: '0.9rem',
                fontWeight: isSelected(menuItem.path) ? '600' : '400',
                transition: 'all 0.2s ease',
              },
            }}
          />
        )}

        {/* Animated chevron for children */}
        {menuItem.children && !isCollapsed && (
          <ListItemIcon sx={{ marginLeft: 'auto', minWidth: '24px' }}>
            <Box
              sx={{
                transform: openChildMenus[menuItem.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '18px' }} />
            </Box>
          </ListItemIcon>
        )}
      </ListItemButton>

      {/* Enhanced child menu with better animation */}
      {menuItem.children && !isCollapsed && (
  <Box
    className="child-menu"
    sx={{
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      maxHeight: openChildMenus[menuItem.id] ? '500px' : '0',
      opacity: openChildMenus[menuItem.id] ? 1 : 0,
      display: openChildMenus[menuItem.id] ? 'block' : 'none', // hide completely when closed
      // margin: '0 12px', // remove this
    }}
  >
          {menuItem.children.map((child) => (
            <Box key={child.id} sx={{ pl: 3 }}>
              <ListItemButton
                onClick={() => child.path && navigate(child.path)}
                sx={{
                  padding: '10px 16px',
                  margin: '2px 0',
                  borderRadius: '6px',
                  backgroundColor: isSelected(child.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  color: isSelected(child.path) ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    paddingLeft: '20px',
                  },
                  '&::before': isSelected(child.path) ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '60%',
                    backgroundColor: '#fff',
                    borderRadius: '0 2px 2px 0',
                  } : {},
                }}
              >
                <ListItemText
                  primary={child.label}
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: '0.85rem',
                      fontWeight: isSelected(child.path) ? '500' : '400',
                    }
                  }}
                />
              </ListItemButton>
            </Box>
          ))}
        </Box>
      )}
    </li>
  );

  return (
    <Drawer
      variant="persistent"
      open={!isCollapsed}
      sx={{
        width: !isCollapsed ? 260 : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 260,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isCollapsed ? 'translateX(-100%)' : 'translateX(0)',
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.2)',
          background: colors.sidebarBg,
          border: 'none',
          overflowX: 'hidden',
          color: '#fff',
          ...sx,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 75,
          background: colors.sidebarBg,
          color: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img src={logoImage} alt="Logo" width={130} />
      </Box>

      {/* Menu area */}
      <Box
        sx={{
          flex: 1,
          background: colors.sidebarBg,
          position: 'relative',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '2px',
          },
        }}
      >
        {((!isMobile && isCollapsed) || (!isMobile && !isCollapsed) || (isMobile && !isCollapsed)) && (
          <List
            component="ul"
            sx={{
              padding: '8px',
              '& > li:first-of-type': {
                marginTop: '8px',
              },
              '& .MuiListItem-root:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiListItemIcon-root': {
                color: colors.accent,
              },
            }}
          >
            {menu.staticMenu.map((menuItem: MenuItem) => renderMenu(menuItem))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          padding: '16px',
          borderTop: '1px solid rgba(249, 249, 244, 0.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#ffff",
            fontWeight: 500,
            fontSize: '0.75rem',
          }}
        >
          {config.versionInfo.appName} {config.versionInfo.version} {year}
        </Typography>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
